import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Camera, RotateCcw } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';

type MobileCaptureData = {
  captures: Array<{ id: string; captureType: string; photoUrl?: string | null; noteText?: string | null; workflowStatus: string; createdAt: string }>;
};

export default function MobileCapturePage() {
  const { fetchJson } = useApiClient();
  const [data, setData] = useState<MobileCaptureData | null>(null);
  const [error, setError] = useState('');
  const [captureType, setCaptureType] = useState<'PHOTO' | 'QUICK_NOTE' | 'NURSE_WORKFLOW'>('PHOTO');
  const [photoUrl, setPhotoUrl] = useState('');
  const [noteText, setNoteText] = useState('');

  async function load() {
    try {
      setError('');
      setData(await fetchJson<MobileCaptureData>('/mobile-capture'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load mobile captures.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function handlePhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoUrl(`mobile-photo:${file.name}`);
    if (!noteText.trim()) setNoteText(file.name);
  }

  async function saveCapture(event: FormEvent) {
    event.preventDefault();
    await fetchJson('/mobile-capture', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        captureType,
        photoUrl: captureType === 'PHOTO' ? photoUrl : undefined,
        noteText: captureType === 'PHOTO' ? noteText || undefined : noteText,
        workflowStatus: captureType === 'NURSE_WORKFLOW' ? 'NEEDS_REVIEW' : 'CAPTURED',
      }),
    });
    setPhotoUrl('');
    setNoteText('');
    await load();
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mobile capture</p>
          <h1 className="text-2xl font-semibold text-slate-900">Photos, quick notes, and nurse workflow capture</h1>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium"><RotateCcw size={16} /> Refresh</button>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <form onSubmit={saveCapture} className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          {(['PHOTO', 'QUICK_NOTE', 'NURSE_WORKFLOW'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setCaptureType(type)}
              className={`rounded-md border px-3 py-2 text-sm font-semibold ${captureType === type ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'}`}
            >
              {type.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
        {captureType === 'PHOTO' ? (
          <label className="block rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-700">
            <span className="mb-2 flex items-center gap-2 font-semibold"><Camera size={18} /> Capture photo</span>
            <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="w-full text-sm" />
            {photoUrl ? <span className="mt-2 block text-slate-500">{photoUrl}</span> : null}
          </label>
        ) : null}
        <textarea value={noteText} onChange={(event) => setNoteText(event.target.value)} placeholder="Quick note or nurse workflow details" className="min-h-28 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <button className="w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white sm:w-auto">Save capture</button>
      </form>

      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Camera size={16} /> Recent mobile captures</div>
        <div className="mt-3 divide-y divide-slate-100">
          {(data?.captures || []).length ? data?.captures.map((row) => (
            <div key={row.id} className="py-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-slate-700">{row.captureType.replace(/_/g, ' ')}</span>
                <span className="text-slate-500">{row.workflowStatus}</span>
              </div>
              <div className="mt-1 text-slate-500">{row.noteText || row.photoUrl || 'Captured'}</div>
            </div>
          )) : <div className="py-3 text-sm text-slate-500">No rows yet.</div>}
        </div>
      </div>
    </div>
  );
}
